import {Component, Inject, ViewChild, OnInit} from '@angular/core';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {finalize, take} from 'rxjs/operators';
import {TableControl} from '../../../../common/models/TableControl';
import {ReportingService} from '../../../../other/services/reporting.service';
import {SharedService} from '../../../../common/services/shared-service';
import {detailsExpand} from '../../../../common';
import {DateUtil} from '../../../../common/util/DateUtil';
import {ApplicationPersonDTO} from '../../../models/application/ApplicationPersonDTO';
import {ApplicationRuleMessageDTO, RuleMessageDialogData} from '../../../models/application/ApplicationRuleMessageDTO';
import {getStatusColor} from 'src/app/modules/common/util/Util';

enum TableColumns {
  ApplicationPersonId = 'applicationPersonId',
  RuleNumber = 'ruleNumber',
  RulePackage = 'rulePackage',
  ReasonCode = 'reasonCode',
  ReasonDescription = 'reasonDescription',
  ActionCode = 'actionCode',
  OverrideIndicator = 'overrideIndicator',
  LastFired = 'lastFired'
}

@Component({
  selector: 'app-rule-message-dialog',
  templateUrl: './rule-message-dialog.component.html',
  styleUrls: ['../../../../../../styles/global/_dialog-styles.scss'],
  animations: [detailsExpand]
})
export class RuleMessageDialogComponent implements OnInit {
  readonly getStatusColor = getStatusColor;
  readonly getActionCodeColour = getStatusColor;

  readonly tableColumns = TableColumns;
  readonly dateFormat = DateUtil.dateTimeFormat;

  displayedColumns: string[] = Object.values(TableColumns);
  dataSource: MatTableDataSource<ApplicationRuleMessageDTO>;
  tableControl: TableControl;
  applicationId: number;
  stage: string;
  status: string;
  statusForIndicator: string;
  showDownloadButton: boolean;
  isBusyCounterInlineSpinner = 0;

  length = 100;
  pageSize = 10;
  pageNumber = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  constructor(
    private reportingService: ReportingService,
    public dialogRef: MatDialogRef<RuleMessageDialogComponent>,
    public sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public data: RuleMessageDialogData
  ) {
    this.tableControl = new TableControl(
      this.length,
      this.pageSize,
      this.pageNumber,
      this.pageSizeOptions,
      'ruleNumber',
      'asc',
      'applicationId',
      ''
    );

    this.stage = data?.stage;
    this.status = data?.status;
    this.statusForIndicator = data?.application?.status?.statusName;
    this.applicationId = data?.application.applicationId;
    this.showDownloadButton = data?.showDownloadButton;
    this.dataSource = new MatTableDataSource(this.getSortedRuleMessages(data?.application.ruleMessages));
    this.tableControl.length = this.dataSource.data?.length;
  }

  getActionPriority(action: string): number {
    switch (action) {
      case 'Reject': return 1;
      case 'Pend': return 2;
      case 'Warn': return 3;
      case 'SetValue': return 4;
      case 'Information': return 5;
      default: return 999;
    }
  }

  ngOnInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.dataSource.filterPredicate = (data: ApplicationRuleMessageDTO, filter: string) => {
      switch (filter) {
      case '1':         // Active Rules
        return data.isCurrentIndicator;
      case '0':         // Inactive Rules
        return !data.isCurrentIndicator;
      case '-1':        // All Rules
      default:
        return true;
      }
    };

    // Apply initial Default "Active Rules" filter
    this.dataSource.filter = '1';
  }

  pageChanged($event: PageEvent) {
    this.tableControl.pageNumber = $event.pageIndex;
    this.tableControl.pageSize = $event.pageSize;
  }

  onCloseClick() {
    this.dialogRef.close();
  }

  onGenerateReportClick(): void {
    if (!this.applicationId) {
      return;
    }
    this.isBusyCounterInlineSpinner++;
    this.reportingService.generateApplicationMessagesReport(this.applicationId)
      .pipe(
        take(1),
        finalize(() => this.isBusyCounterInlineSpinner--)
      )
      .subscribe({
        next: () => {
          this.sharedService.openSnackBar('Report generated successfully', 'Dismiss');
        },
        error: () => {
          this.sharedService.openSnackBar('Error generating report', 'Dismiss');
        }
      });
  }

  coalesce(a: Date, b: Date) {
    return a != null ? a : b;
  }

  onStatusSelectionChange(status: string) {
    this.dataSource.filter = status;

    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  private getSortedRuleMessages(ruleMessages: ApplicationRuleMessageDTO[]): ApplicationRuleMessageDTO[] {
    if (!ruleMessages?.length) {
      return [];
    }

    return ruleMessages
      .map((rule: ApplicationRuleMessageDTO) => ({
        ...rule,
        displayPersonName: this.getPersonName(rule.applicationPersonId),          // Info: FE Usage - display person FullName
        actionPriority: this.getActionPriority(rule.actionCode)                   // Info: FE Usage - determine rule message priority based on action code
      }))
      .sort((a: any, b: any) => (
        Number(b.isCurrentIndicator) - Number(a.isCurrentIndicator) ||            // (1) Current rules first
        a.actionPriority - b.actionPriority ||                                    // (2) Higher priority action codes first
        a.displayPersonName?.localeCompare(b.displayPersonName || '') ||     // (3) Person Name in ascending order, null values last
        b.applicationRuleMessageId - a.applicationRuleMessageId                   // (4) Latest Rule Message first
      )) || [];
  }

  private getPersonName(personId: number | null): string {
    if (!personId) {
      return 'N/A';
    }
    const person: ApplicationPersonDTO = this.data.application.membership?.persons?.find(p => p.applicationPersonId === personId);

    if (!person) {
      return 'N/A';
    }

    return `${person.firstName} ${person.middleName || ''} ${person.lastName}`.trim();
  }

}
